import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Upload,
  Loader2,
  Trash2,
  FileText,
  Image as ImageIcon,
  Video,
  Music,
  Paperclip,
  Download,
  X,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_ICON = {
  image: ImageIcon,
  video: Video,
  audio: Music,
  document: FileText,
  file: Paperclip,
};

const fmtBytes = (n) => {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

/**
 * MediaUploader
 * -------------
 * One reusable dropzone + gallery used across CRM cards, Client Portal
 * deliverables, booking briefs, and (with `publicToken`) the podcast
 * guest form. Uploads go to /api/media/upload (or /public/upload when
 * `publicToken` is set) with an `owner_type` + `owner_id` scope so the
 * backend can enforce ownership rules on both list and download.
 *
 * Props:
 *   ownerType   'card' | 'client' | 'booking' | 'guest_request'
 *   ownerId     card_id, client email, booking id, or guest email
 *   authMode    'admin' (default), 'client' (uses cookie), 'public'
 *   publicToken required when authMode='public' — server-issued signed token
 *   accept      HTML accept string (defaults to a permissive set)
 *   maxFiles    soft limit before the picker refuses new files
 *   compact     tighter styling for inline use (cards, modals)
 *   onChange    (files[]) => void, called after any upload/delete
 */
const MediaUploader = ({
  ownerType,
  ownerId,
  authMode = "admin",
  publicToken = null,
  accept = "image/*,video/*,audio/*,application/pdf,.txt,.doc,.docx,.rtf",
  maxFiles = 30,
  compact = false,
  onChange = () => {},
  hideList = false,
  title = "Files",
  emptyLabel = "Nothing here yet — drop a file to start.",
  testIdPrefix = "media",
}) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const inputRef = useRef(null);
  const dropRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const headers = useCallback(() => {
    if (authMode === "admin") {
      const t = localStorage.getItem("sb_admin_token") || "";
      return t ? { Authorization: `Bearer ${t}` } : {};
    }
    return {}; // client uses cookie session; public uses signed token in body
  }, [authMode]);

  const config = useCallback(() => {
    const opts = { headers: headers() };
    if (authMode === "client") opts.withCredentials = true;
    return opts;
  }, [authMode, headers]);

  const load = useCallback(async () => {
    if (authMode === "public" || !ownerId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/media?owner_type=${encodeURIComponent(ownerType)}&owner_id=${encodeURIComponent(ownerId)}`,
        config(),
      );
      setFiles(data.files || []);
      onChange(data.files || []);
    } catch (e) {
      // Silent — a client with no files is a normal state
      if (e?.response?.status !== 404) {
        // eslint-disable-next-line no-console
        console.warn("media list failed", e?.response?.data || e.message);
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authMode, ownerType, ownerId, config]);

  useEffect(() => {
    load();
  }, [load]);

  const handleFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    if (files.length + fileList.length > maxFiles) {
      toast.error(`Max ${maxFiles} files per item.`);
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      for (const f of fileList) {
        const form = new FormData();
        form.append("file", f);
        if (authMode === "public") {
          if (!publicToken) {
            toast.error("Missing upload token.");
            break;
          }
          form.append("token", publicToken);
        } else {
          form.append("owner_type", ownerType);
          form.append("owner_id", ownerId);
        }
        const url =
          authMode === "public"
            ? `${API}/media/public/upload`
            : `${API}/media/upload`;
        const opts = {
          ...config(),
          onUploadProgress: (e) => {
            if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
          },
        };
        const { data } = await axios.post(url, form, opts);
        setFiles((prev) => [data, ...prev]);
        onChange([data, ...files]);
      }
      toast.success(`Uploaded ${fileList.length} file${fileList.length > 1 ? "s" : ""}.`);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Upload failed. Try a smaller file or check your connection.",
      );
    } finally {
      setUploading(false);
      setProgress(0);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const removeFile = async (id) => {
    if (!window.confirm("Remove this file? It won't be recoverable.")) return;
    try {
      await axios.delete(`${API}/media/${id}`, config());
      const next = files.filter((f) => f.id !== id);
      setFiles(next);
      onChange(next);
      toast.success("Removed.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(Array.from(e.dataTransfer.files || []));
  };

  const authQueryFor = (fileId) => {
    // Build a URL <img> can consume — pass Bearer via ?auth for admin,
    // rely on cookie for client, no auth for public (image is public
    // once uploaded — guest headshots).
    if (authMode === "admin") {
      const t = localStorage.getItem("sb_admin_token") || "";
      return t ? `?auth=${encodeURIComponent(t)}` : "";
    }
    return "";
  };

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-uploader`}>
      {title && !compact && (
        <div className="flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#a395c3]">
            {title}
          </div>
          <div className="text-[10px] text-white/40">
            {files.length}/{maxFiles}
          </div>
        </div>
      )}

      {/* Dropzone */}
      <div
        ref={dropRef}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        data-testid={`${testIdPrefix}-dropzone`}
        className={`cursor-pointer rounded-2xl border border-dashed transition-colors ${
          compact ? "p-3" : "p-5"
        } ${
          dragOver
            ? "border-[#00f0ff]/60 bg-[#00f0ff]/[0.06]"
            : "border-white/15 bg-white/[0.02] hover:border-white/25 hover:bg-white/[0.04]"
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={accept}
          data-testid={`${testIdPrefix}-input`}
          className="hidden"
          onChange={(e) => handleFiles(Array.from(e.target.files || []))}
        />
        <div className="flex items-center gap-3">
          <div className={`rounded-xl bg-[#00f0ff]/12 border border-[#00f0ff]/30 flex items-center justify-center flex-shrink-0 ${compact ? "w-9 h-9" : "w-11 h-11"}`}>
            {uploading ? (
              <Loader2 size={16} className="animate-spin text-[#00f0ff]" />
            ) : (
              <Upload size={16} className="text-[#00f0ff]" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className={`font-semibold ${compact ? "text-[13px]" : "text-sm"}`}>
              {uploading ? `Uploading… ${progress}%` : "Drop files or tap to browse"}
            </div>
            <div className={`text-white/50 ${compact ? "text-[10px]" : "text-[11px]"}`}>
              {authMode === "public"
                ? "Images or PDF · up to 25 MB"
                : "Any file · up to 250 MB"}
            </div>
          </div>
        </div>
        {uploading && (
          <div className="mt-3 h-1 rounded-full bg-white/10 overflow-hidden">
            <div
              className="h-full bg-[#00f0ff] transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
      </div>

      {/* Files gallery */}
      {!hideList && (
        <div className="space-y-2">
          {loading ? (
            <div className="text-white/40 text-sm py-3 flex items-center gap-2">
              <Loader2 size={12} className="animate-spin" /> Loading files…
            </div>
          ) : files.length === 0 ? (
            <div className="text-white/40 text-[12px] italic py-2">{emptyLabel}</div>
          ) : (
            files.map((f) => {
              const Icon = KIND_ICON[f.kind] || Paperclip;
              const canDelete = authMode !== "public";
              const src = `${API}/media/${f.id}/download${authQueryFor(f.id)}`;
              return (
                <div
                  key={f.id}
                  data-testid={`${testIdPrefix}-file-${f.id}`}
                  className="flex items-center gap-3 p-2.5 rounded-xl bg-white/[0.02] border border-white/10 hover:bg-white/[0.04] transition-colors"
                >
                  <div className="w-11 h-11 rounded-lg overflow-hidden flex items-center justify-center flex-shrink-0 bg-white/[0.05] border border-white/10">
                    {f.kind === "image" && authMode !== "public" ? (
                      <img
                        src={src}
                        alt={f.filename}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <Icon size={16} className="text-white/60" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-medium truncate">
                      {f.filename}
                    </div>
                    <div className="text-[10px] text-white/40 mt-0.5">
                      {fmtBytes(f.size)} · {f.kind}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <a
                      href={src}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`${testIdPrefix}-open-${f.id}`}
                      aria-label="Open file"
                      title="Open"
                      className="w-8 h-8 rounded-lg bg-white/[0.05] border border-white/10 hover:bg-[#00f0ff]/15 hover:text-[#00f0ff] hover:border-[#00f0ff]/40 flex items-center justify-center transition-colors"
                    >
                      <Download size={13} />
                    </a>
                    {canDelete && (
                      <button
                        type="button"
                        onClick={() => removeFile(f.id)}
                        data-testid={`${testIdPrefix}-delete-${f.id}`}
                        aria-label="Delete file"
                        title="Delete"
                        className="w-8 h-8 rounded-lg bg-white/[0.05] border border-white/10 hover:bg-[#ff0055]/15 hover:text-[#ff0055] hover:border-[#ff0055]/40 flex items-center justify-center transition-colors"
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default MediaUploader;
export { X };
